import { api } from "./api";
import type {
  TranscriptionProvider,
  TranscriptionProviderUpdate,
} from "@/lib/transcriptionProvider";
import type {
  Invite,
  Organization,
  User,
  SystemStats,
  ConnectorStats,
  TokenUsageStats,
  PaginatedResponse,
  UserFilters,
  UserTitleOption,
  ToolPrompt,
  ToolPromptResponse,
  ToolPromptUpdateRequest,
  ToolPromptUpdateResponse,
  CreateToolPromptRequest,
  CreateToolPromptResponse,
  Prompt,
  CreatePromptRequest,
  UpdatePromptRequest,
  PromptTestRequest,
  PromptTestResponse,
  ClearCacheRequest,
  PromptVersion,
  PromptVersionHistory,
  PromptDiff,
  VersionComparison,
  RevertPromptRequest,
  RevertPromptResponse,
  Connector,
  ConnectorsResponse,
  EnvConfig,
  BulkToolPromptsCsvResponse,
  BulkImportPromptsCsvResponse,
  Provider,
  Model,
  CreateProviderInput,
  UpdateProviderInput,
  CreateModelInput,
  UpdateModelInput,
  ModelType,
  TokenUsageOverview,
  TokenUsageOrganizationsResponse,
  TokenUsageOrganizationDetail,
  TokenUsageUsersResponse,
  TokenUsageUserDetail,
  TokenUsagePurposesResponse,
  TokenUsageModelsResponse,
  TokenUsageDailyBreakdown,
  AvailablePurposesResponse,
  CostEventsResponse,
  CostEventsFilters,
  MissingPricingResponse,
  OrganizationUsersResponse,
  UserChat,
  UserChatsResponse,
  UserServiceUsageResponse,
  UsageSession,
  UserSessionsResponse,
  ActiveSessionResponse,
  UserUsageStatus,
  SessionActivityResponse,
  EmailAssistantGlobalConfig,
  IngestionScopeConfig,
  IngestionScopeConfigUpdate,
  RateLimitConfig,
  SubscriptionPlanConfig,
  EmbeddingRouterStats,
  SlideGenerationTrace,
  SlideGenerationTraceStatus,
  SlideTracesListResponse,
  ImageModelSpec,
  ImageModelSpecInput,
  MeetingRecordingRoutingPolicy,
  MeetingRoutingHealth,
  BulkOrganizationFeatureFlagOverrideResponse,
  BulkOrganizationFeatureFlagSyncResponse,
  FeatureFlagsGlobalResponse,
  FeatureFlagOrganizationsResponse,
  FeatureFlagOrganizationSummary,
  FeatureFlagOverrideState,
  OrganizationFeatureFlagSyncResponse,
  OrganizationFeatureFlagsResponse,
} from "../types/admin";

const API_BASE = "/admin";

export interface SidebarItem {
  key: string;
  visible: boolean;
}

export interface NativeAgentRow {
  id: string;
  slug: string;
  name: string;
  personaName: string | null;
  isActive: boolean;
  isInternal: boolean;
  canSpawnAgents: boolean;
  productModelId: string | null;
}

export interface CustomAgentRow {
  id: string;
  name: string;
  isActive: boolean;
  productModelId: string | null;
  organization: { id: string; name: string } | null;
  createdAt: string;
}

export const adminApi = {
  // Native meeting bot — recording routing policy + health
  async getMeetingRoutingPolicy(): Promise<MeetingRecordingRoutingPolicy> {
    const response = await api.get(`${API_BASE}/meeting-routing-policy`);
    return response.data;
  },

  async updateMeetingRoutingPolicy(
    patch: Partial<
      Pick<
        MeetingRecordingRoutingPolicy,
        | "mode"
        | "googleMeetProvider"
        | "zoomProvider"
        | "teamsProvider"
        | "fallbackEnabled"
        | "adHocFallbackDeadlineSeconds"
        | "scheduledFallbackDeadlineSeconds"
      >
    >,
  ): Promise<MeetingRecordingRoutingPolicy> {
    const response = await api.put(`${API_BASE}/meeting-routing-policy`, patch);
    return response.data;
  },

  async getMeetingRoutingHealth(): Promise<MeetingRoutingHealth> {
    const response = await api.get(`${API_BASE}/meeting-routing-health`);
    return response.data;
  },

  // Organizations
  async getOrganizations(): Promise<Organization[]> {
    const response = await api.get(`${API_BASE}/organizations`);
    return response.data;
  },

  async getOrganization(id: string): Promise<Organization> {
    const response = await api.get(`${API_BASE}/organizations/${id}`);
    return response.data;
  },

  async updateOrganization(
    id: string,
    data: Partial<Organization>,
  ): Promise<Organization> {
    const response = await api.put(`${API_BASE}/organizations/${id}`, data);
    return response.data;
  },

  async deleteOrganization(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/organizations/${id}`);
    return response.data;
  },

  // Users
  async getUsers(filters: UserFilters = {}): Promise<PaginatedResponse<User>> {
    const response = await api.get(`${API_BASE}/users`, { params: filters });
    return response.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await api.get(`${API_BASE}/users/${id}`);
    return response.data;
  },

  async getUserTitleOptions(): Promise<UserTitleOption[]> {
    const response = await api.get(`${API_BASE}/users/title-options`);
    return response.data.titles;
  },

  async updateUser(id: string, data: Partial<Omit<User, 'role' | 'isSuperadmin'>>): Promise<User> {
    const response = await api.put(`${API_BASE}/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/users/${id}`);
    return response.data;
  },

  async getUserChats(
    userId: string,
    page: number = 1,
    limit: number = 10,
    days?: number,
  ): Promise<UserChatsResponse> {
    const params: any = { page, limit };
    if (days) {
      params.days = days;
    }
    const response = await api.get(`${API_BASE}/users/${userId}/chats`, {
      params,
    });
    return response.data;
  },

  async getUserServiceUsage(
    userId: string,
    page: number = 1,
    limit: number = 10,
    days?: number,
  ): Promise<UserServiceUsageResponse> {
    const params: any = { page, limit };
    if (days) {
      params.days = days;
    }
    const response = await api.get(
      `${API_BASE}/users/${userId}/service-usage`,
      { params },
    );
    return response.data;
  },

  // Sessions
  async getUserSessions(
    userId: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<UserSessionsResponse> {
    const response = await api.get(
      `${API_BASE}/users/${userId}/sessions`,
      { params: { page, limit } },
    );
    return response.data;
  },

  async getUserActiveSession(userId: string): Promise<ActiveSessionResponse> {
    const response = await api.get(
      `${API_BASE}/users/${userId}/sessions/active`,
    );
    return response.data;
  },

  async getUserUsageStatus(userId: string): Promise<UserUsageStatus> {
    const response = await api.get(
      `${API_BASE}/users/${userId}/usage-status`,
    );
    return response.data;
  },

  async getSessionActivity(
    userId: string,
    sessionId: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<SessionActivityResponse> {
    const response = await api.get(
      `${API_BASE}/users/${userId}/sessions/${sessionId}/activity`,
      { params: { page, limit } },
    );
    return response.data;
  },

  // Statistics
  async getSystemStats(): Promise<SystemStats> {
    const response = await api.get(`${API_BASE}/stats`);
    return response.data;
  },

  async getConnectorStats(): Promise<ConnectorStats> {
    const response = await api.get(`${API_BASE}/connectors/stats`);
    return response.data;
  },

  async getConnectorsMetadata(): Promise<any> {
    const response = await api.get(`${API_BASE}/connectors/metadata`);
    return response.data;
  },

  async getConnectors(organizationId?: string): Promise<ConnectorsResponse> {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get(`${API_BASE}/connectors`, { params });
    return response.data;
  },

  async syncConnector(id: string): Promise<{ message: string }> {
    const response = await api.get(`/connectors/sadmin/${id}/sync`);
    return response.data;
  },

  async deleteConnector(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/connectors/${id}`);
    return response.data;
  },

  async getResyncOverview(params?: { organizationId?: string; type?: string }): Promise<any> {
    const response = await api.get(`${API_BASE}/connectors/resync/overview`, { params });
    return response.data;
  },

  async bulkResync(connectorIds: string[]): Promise<{ message: string; total: number; connectorIds: string[] }> {
    const response = await api.post(`${API_BASE}/connectors/resync/bulk`, { connectorIds });
    return response.data;
  },

  async getTokenUsageStats(
    period: number = 30,
    organizationId?: string,
  ): Promise<TokenUsageStats> {
    const params: Record<string, any> = { period };
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get(`${API_BASE}/token-usage/stats`, { params });
    return response.data;
  },

  async getTokenUsageOverview(params?: {
    period?: number;
    organizationId?: string;
    userId?: string;
    purpose?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<TokenUsageOverview> {
    const response = await api.get(`${API_BASE}/token-usage/overview`, {
      params,
    });
    return response.data;
  },

  async getTokenUsageOrganizations(params?: {
    period?: number;
    purpose?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    limit?: number;
    offset?: number;
  }): Promise<TokenUsageOrganizationsResponse> {
    const response = await api.get(`${API_BASE}/token-usage/organizations`, {
      params,
    });
    return response.data;
  },

  async getTokenUsageOrganizationDetail(
    organizationId: string,
    params?: {
      period?: number;
      purpose?: string;
      includeUsers?: boolean;
      includeDaily?: boolean;
    },
  ): Promise<TokenUsageOrganizationDetail> {
    const response = await api.get(
      `${API_BASE}/token-usage/organizations/${organizationId}`,
      { params },
    );
    return response.data;
  },

  async getTokenUsageUsers(params?: {
    period?: number;
    organizationId?: string;
    purpose?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    limit?: number;
    offset?: number;
  }): Promise<TokenUsageUsersResponse> {
    const response = await api.get(`${API_BASE}/token-usage/users`, { params });
    return response.data;
  },

  async getTokenUsageUserDetail(
    userId: string,
    params?: {
      period?: number;
      includePurposes?: boolean;
      includeDaily?: boolean;
      includeModels?: boolean;
    },
  ): Promise<TokenUsageUserDetail> {
    const response = await api.get(`${API_BASE}/token-usage/users/${userId}`, {
      params,
    });
    return response.data;
  },

  async getTokenUsagePurposes(params?: {
    period?: number;
    organizationId?: string;
    userId?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<TokenUsagePurposesResponse> {
    const response = await api.get(`${API_BASE}/token-usage/purposes`, {
      params,
    });
    return response.data;
  },

  async getTokenUsageModels(params?: {
    period?: number;
    organizationId?: string;
    userId?: string;
    purpose?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<TokenUsageModelsResponse> {
    const response = await api.get(`${API_BASE}/token-usage/models`, {
      params,
    });
    return response.data;
  },

  async getTokenUsageDaily(params?: {
    period?: number;
    organizationId?: string;
    userId?: string;
    purpose?: string;
  }): Promise<TokenUsageDailyBreakdown> {
    const response = await api.get(`${API_BASE}/token-usage/daily`, { params });
    return response.data;
  },

  async getAvailablePurposes(): Promise<AvailablePurposesResponse> {
    const response = await api.get(`${API_BASE}/token-usage/purposes/list`);
    return response.data;
  },

  // Row-level cost event explorer.
  async getCostEvents(
    filters: CostEventsFilters = {},
  ): Promise<CostEventsResponse> {
    const response = await api.get(`${API_BASE}/cost-events`, {
      params: filters,
    });
    return response.data;
  },

  // Models that produced cost events with no ModelPricing match.
  async getMissingPricingModels(params?: {
    period?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<MissingPricingResponse> {
    const response = await api.get(
      `${API_BASE}/cost-events/missing-pricing`,
      { params },
    );
    return response.data;
  },

  async getOrganizationUsers(
    organizationId: string,
    params?: {
      includeStats?: boolean;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<OrganizationUsersResponse> {
    const response = await api.get(
      `${API_BASE}/token-usage/organizations/${organizationId}/users`,
      { params },
    );
    return response.data;
  },

  // Config Management
  async getOrganizationConfig(organizationId: string): Promise<EnvConfig[]> {
    const response = await api.get(
      `${API_BASE}/config/${encodeURIComponent(organizationId)}`,
    );
    return response.data;
  },

  async setOrganizationConfig(
    organizationId: string,
    key: string,
    value: string,
  ): Promise<{ message: string }> {
    const response = await api.post(
      `${API_BASE}/config/${encodeURIComponent(organizationId)}`,
      { key, value },
    );
    return response.data;
  },

  async deleteOrganizationConfig(
    organizationId: string,
    key: string,
  ): Promise<{ message: string }> {
    const response = await api.delete(
      `${API_BASE}/config/${encodeURIComponent(organizationId)}/${encodeURIComponent(key)}`,
    );
    return response.data;
  },

  // Logs
  async getLogs(
    page: number = 1,
    limit: number = 50,
    level?: string,
    search?: string,
  ) {
    const params: Record<string, any> = { page, limit };
    if (level) params.level = level;
    if (search) params.search = search;

    const response = await api.get(`${API_BASE}/logs`, { params });
    return response.data;
  },

  // Tool Prompts
  async getToolPrompts(): Promise<ToolPromptResponse> {
    const response = await api.get(`${API_BASE}/tools`);
    return response.data;
  },

  async getToolPrompt(
    toolName: string,
    connectorType: string,
  ): Promise<ToolPrompt> {
    const response = await api.get(
      `${API_BASE}/tools/${toolName}/${connectorType}`,
    );
    return response.data;
  },

  async updateToolPrompt(
    toolName: string,
    connectorType: string,
    data: ToolPromptUpdateRequest,
  ): Promise<ToolPromptUpdateResponse> {
    const response = await api.put(
      `${API_BASE}/tools/${toolName}/${connectorType}`,
      data,
    );
    return response.data;
  },

  async createToolPrompt(
    connectorType: string,
    data: CreateToolPromptRequest,
  ): Promise<CreateToolPromptResponse> {
    const response = await api.post(
      `${API_BASE}/tools/${encodeURIComponent(connectorType)}`,
      data,
    );
    return response.data;
  },

  async bulkCreateToolPromptsFromCsv(
    file: File,
  ): Promise<BulkToolPromptsCsvResponse> {
    const form = new FormData();
    form.append("file", file);
    const response = await api.post(`${API_BASE}/tools/import/csv`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  async exportToolPromptsCsv(): Promise<string> {
    const response = await api.get(`${API_BASE}/tools/export/csv`, {
      responseType: "text",
    });
    return response.data;
  },

  // Prompt Management
  async getPrompts(): Promise<Prompt[]> {
    const response = await api.get(`${API_BASE}/prompts`);
    return response.data;
  },

  async getPrompt(id: string): Promise<Prompt> {
    const response = await api.get(`${API_BASE}/prompts/${id}`);
    return response.data;
  },

  async getPromptsByIdentifier(identifier: string): Promise<Prompt[]> {
    const response = await api.get(
      `${API_BASE}/prompts/identifier/${identifier}`,
    );
    return response.data;
  },

  async createPrompt(data: CreatePromptRequest): Promise<Prompt> {
    const response = await api.post(`${API_BASE}/prompts`, data);
    return response.data;
  },

  async updatePrompt(id: string, data: UpdatePromptRequest): Promise<Prompt> {
    const response = await api.put(`${API_BASE}/prompts/${id}`, data);
    return response.data;
  },

  async deletePrompt(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/prompts/${id}`);
    return response.data;
  },

  async testPrompt(
    id: string,
    data: PromptTestRequest,
  ): Promise<PromptTestResponse> {
    const response = await api.post(`${API_BASE}/prompts/${id}/test`, data);
    return response.data;
  },

  async clearPromptCache(
    data: ClearCacheRequest = {},
  ): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/prompts/cache/clear`, data);
    return response.data;
  },

  async bulkImortPrompts(file: File): Promise<BulkImportPromptsCsvResponse> {
    const form = new FormData();
    form.append("file", file);
    const response = await api.post(`${API_BASE}/prompts/import/csv`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  async exportPromptsCsv(): Promise<string> {
    const response = await api.get(`${API_BASE}/prompts/export/csv`, {
      responseType: "text",
    });
    return response.data;
  },

  // Version Management
  async getPromptVersions(identifier: string): Promise<PromptVersionHistory> {
    const response = await api.get(
      `${API_BASE}/prompts/identifier/${identifier}/versions`,
    );
    return response.data;
  },

  async comparePromptVersions(
    identifier: string,
    fromVersion: number,
    toVersion: number,
  ): Promise<VersionComparison> {
    const response = await api.get(
      `${API_BASE}/prompts/identifier/${identifier}/compare/${fromVersion}/${toVersion}`,
    );
    return response.data;
  },

  async getPromptDiff(
    identifier: string,
    fromVersion: number,
    toVersion: number,
  ): Promise<PromptDiff> {
    const response = await api.get(
      `${API_BASE}/prompts/identifier/${identifier}/diff/${fromVersion}/${toVersion}`,
    );
    return response.data;
  },

  async revertPromptToVersion(
    identifier: string,
    version: number,
    data: RevertPromptRequest = {},
  ): Promise<RevertPromptResponse> {
    const response = await api.post(
      `${API_BASE}/prompts/identifier/${identifier}/revert/${version}`,
      data,
    );
    return response.data;
  },

  async updatePromptTemplate(
    id: string,
    template: string,
    changeMessage?: string,
    baseVersion?: number,
    allowPlaceholderChanges?: boolean,
  ): Promise<Prompt> {
    const response = await api.put(`${API_BASE}/prompts/${id}`, {
      template,
      changeMessage,
      baseVersion,
      allowPlaceholderChanges,
    });
    return response.data;
  },

  async getPromptVersion(
    identifier: string,
    version: number,
  ): Promise<PromptVersion> {
    const response = await api.get(
      `${API_BASE}/prompts/identifier/${identifier}/version/${version}`,
    );
    return response.data;
  },
  async getPromptChats(): Promise<{
    data: import("../types/admin").PromptChatListItem[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }> {
    const response = await api.get(`${API_BASE}/prompts/chats`);
    return response.data;
  },

  async getPromptChatMessages(
    chatId: string,
  ): Promise<import("../types/admin").AIChatMessage[]> {
    const response = await api.get(
      `${API_BASE}/prompts/chat/${encodeURIComponent(chatId)}/messages`,
    );
    const payload = response.data;
    const list = Array.isArray(payload)
      ? payload
      : payload?.messages || payload?.data || [];
    const toRole = (r: any): import("../types/admin").AIChatRole => {
      const v = String(r || "").toLowerCase();
      return v === "user" ? "user" : "assistant";
    };
    const blocksToText = (content: any): string => {
      const blocks = content?.blocks;
      if (Array.isArray(blocks)) {
        return blocks
          .filter(
            (b: any) => b?.type === "text" && typeof b?.content === "string",
          )
          .map((b: any) => b.content)
          .join("\n");
      }
      if (typeof content === "string") return content;
      return "";
    };
    return list.map((m: any) => ({
      role: toRole(m.role),
      content: blocksToText(m.content),
    }));
  },

  async streamPromptAIChat(
    promptId: string,
    body: import("../types/admin").AIChatRequest,
    onChunk: (text: string) => void,
    onError?: (error: any) => void,
    onDone?: () => void,
    onDelta?: (eventName: string, payload: any) => void,
    onOpen?: (chatId: string | null, response: Response) => void,
  ): Promise<void> {
    const url = `${api.defaults.baseURL}${API_BASE}/prompts/${encodeURIComponent(promptId)}/ai-chat`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          // Forward cookies for session
        },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!response.ok || !response.body) {
        throw new Error(
          `Chat stream failed: ${response.status} ${response.statusText}`,
        );
      }
      const chatIdHeader = response.headers.get("x-chat-id");
      onOpen && onOpen(chatIdHeader, response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;
      let buffer = "";
      let currentEvent: string | null = null;
      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          // Basic SSE line parsing supporting event: and data:
          const lines = buffer.split("\n");
          // Keep the last partial line in buffer
          buffer = lines.pop() || "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            if (trimmed.startsWith("event:")) {
              currentEvent = trimmed.slice(6).trim();
              continue;
            }
            if (trimmed.startsWith("data:")) {
              const payload = trimmed.slice(5).trim();
              if (payload === "[DONE]") {
                onDone && onDone();
                return;
              }
              try {
                const evt = JSON.parse(payload);
                if (currentEvent && onDelta) {
                  onDelta(currentEvent, evt);
                } else {
                  if (typeof evt === "string") {
                    onChunk(evt);
                  } else if (evt?.text) {
                    onChunk(evt.text);
                  } else if (evt?.content) {
                    onChunk(evt.content);
                  } else if (evt?.type === "error") {
                    onError && onError(evt.error || "Unknown error");
                  }
                }
              } catch {
                // Fallback to raw text
                onChunk(payload);
              }
            } else {
              // Ignore other lines
              continue;
            }
          }
        }
      }
      onDone && onDone();
    } catch (err) {
      onError && onError(err);
      throw err;
    }
  },

  /**
   * Run one AI Prompt Edit turn. Streams the rationale live via
   * `onRationaleDelta`; resolves with the complete `{ rationale, newTemplate }`
   * once the agent finishes (the template is delivered whole, then diffed
   * locally — see ADR-0046).
   */
  async streamPromptEdit(
    promptId: string,
    body: import("../types/admin").PromptEditRequest,
    callbacks: {
      onRationaleDelta?: (text: string) => void;
      onChatId?: (chatId: string | null) => void;
    } = {},
  ): Promise<import("../types/admin").PromptEditResult> {
    const url = `${api.defaults.baseURL}${API_BASE}/prompts/${encodeURIComponent(
      promptId,
    )}/ai-chat`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      credentials: "include",
    });
    if (!response.ok || !response.body) {
      throw new Error(
        `Prompt edit failed: ${response.status} ${response.statusText}`,
      );
    }
    callbacks.onChatId?.(response.headers.get("x-chat-id"));

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let currentEvent: string | null = null;
    let result: import("../types/admin").PromptEditResult | null = null;
    let done = false;

    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;
      if (!value) continue;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (trimmed.startsWith("event:")) {
          currentEvent = trimmed.slice(6).trim();
          continue;
        }
        if (!trimmed.startsWith("data:")) continue;
        const payload = trimmed.slice(5).trim();
        let evt: any;
        try {
          evt = JSON.parse(payload);
        } catch {
          currentEvent = null;
          continue;
        }
        if (currentEvent === "rationale_delta") {
          if (typeof evt?.text === "string") callbacks.onRationaleDelta?.(evt.text);
        } else if (currentEvent === "prompt_edit_result") {
          result = {
            rationale: evt?.rationale ?? "",
            newTemplate: evt?.newTemplate ?? "",
          };
        } else if (currentEvent === "error") {
          throw new Error(evt?.error?.message || "Prompt edit error");
        }
        currentEvent = null;
      }
    }

    if (!result) {
      throw new Error("Prompt edit ended without returning a revised template");
    }
    return result;
  },

  async getPromptTokenSize(
    identifier: string,
  ): Promise<import("../types/admin").PromptTokenSizeResponse> {
    const response = await api.get(
      `${API_BASE}/prompts/token-size/${encodeURIComponent(identifier)}`,
    );
    return response.data;
  },

  // Admin Environment Variables
  async getAdminEnvVariables(): Promise<any[]> {
    const response = await api.get(`${API_BASE}/config`);
    return response.data;
  },

  async createAdminEnvVariable(envVar: {
    key: string;
    value: string;
    description?: string;
  }): Promise<any> {
    const response = await api.post(`${API_BASE}/config`, envVar);
    return response.data;
  },

  async updateAdminEnvVariable(key: string, value: string): Promise<any> {
    const response = await api.put(`${API_BASE}/config/${key}`, { value });
    return response.data;
  },

  async deleteAdminEnvVariable(key: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/config/${key}`);
    return response.data;
  },

  // Provider Management
  async getProviders(): Promise<Provider[]> {
    const response = await api.get(`${API_BASE}/providers`);
    return response.data?.data || response.data;
  },

  async createProvider(input: CreateProviderInput): Promise<Provider> {
    const response = await api.post(`${API_BASE}/providers`, input);
    return response.data?.data || response.data;
  },

  async updateProvider(
    id: string,
    input: UpdateProviderInput,
  ): Promise<Provider> {
    const response = await api.put(
      `${API_BASE}/providers/${encodeURIComponent(id)}`,
      input,
    );
    return response.data?.data || response.data;
  },

  async deleteProvider(id: string): Promise<{ message: string }> {
    const response = await api.delete(
      `${API_BASE}/providers/${encodeURIComponent(id)}`,
    );
    return response.data;
  },

  // Model Management
  async getModels(params: {
    organizationId: string;
    provider?: string;
    type?: string;
  }): Promise<Model[]> {
    const response = await api.get(`${API_BASE}/models`, { params });
    return response.data?.data || response.data;
  },

  async getModelById(id: string, organizationId: string): Promise<Model> {
    const response = await api.get(
      `${API_BASE}/models/${encodeURIComponent(id)}`,
      { params: { organizationId } },
    );
    return response.data?.data || response.data;
  },

  async createModel(input: CreateModelInput): Promise<Model> {
    const response = await api.post(`${API_BASE}/models`, input);
    return response.data?.data || response.data;
  },

  async updateModel(id: string, input: UpdateModelInput): Promise<Model> {
    const response = await api.put(
      `${API_BASE}/models/${encodeURIComponent(id)}`,
      input,
    );
    return response.data?.data || response.data;
  },

  async deleteModel(id: string): Promise<{ message: string }> {
    const response = await api.delete(
      `${API_BASE}/models/${encodeURIComponent(id)}`,
    );
    return response.data;
  },

  async setModelForAllOrganizations(input: {
    model: string;
    providerId: string;
    type: ModelType;
    config?: Record<string, unknown>;
  }): Promise<{ message: string; count: number }> {
    const response = await api.post(`${API_BASE}/models/org`, input);
    return response.data;
  },

  // Per-org batch transcription/diarization provider (super-admin).
  async setTranscriptionProvider(input: TranscriptionProviderUpdate): Promise<{
    success: boolean;
    organizationId: string;
    applied: { provider: TranscriptionProvider; model: string | null };
  }> {
    const response = await api.post(`${API_BASE}/transcription-provider`, input);
    return response.data;
  },

  async bulkUpdateAgentConfig(data: {
    enableInstructions?: boolean;
    enableToolOutputSummarization?: boolean;
    AIResponseMemory?: boolean;
  }): Promise<{ message: string; count: number }> {
    const response = await api.post(`${API_BASE}/agent-config/bulk`, data);
    return response.data;
  },

  async getAgentConfigSummary(): Promise<{
    totalOrgs: number;
    enableInstructions: number;
    enableToolOutputSummarization: number;
    AIResponseMemory: number;
  }> {
    const response = await api.get(`${API_BASE}/agent-config/summary`);
    return response.data;
  },

  async getConnectorEntitiesInfo(): Promise<Record<string, string[]>> {
    const response = await api.get(`${API_BASE}/connectors/info`);
    return response.data;
  },

  async getModelPricings(): Promise<any[]> {
    const response = await api.get(`${API_BASE}/model-pricing`);
    return response.data?.data || response.data || [];
  },

  async getModelPricing(id: string): Promise<any> {
    const response = await api.get(`${API_BASE}/model-pricing/${id}`);
    return response.data?.data || response.data;
  },

  async createModelPricing(data: {
    model: string;
    inputCost?: number | null;
    outputCost?: number | null;
    cachedInputCost?: number | null;
    cacheWriteCost?: number | null;
  }): Promise<any> {
    const response = await api.post(`${API_BASE}/model-pricing`, data);
    return response.data?.data || response.data;
  },

  async updateModelPricing(
    id: string,
    data: {
      model?: string;
      inputCost?: number | null;
      outputCost?: number | null;
      cachedInputCost?: number | null;
      cacheWriteCost?: number | null;
    },
  ): Promise<any> {
    const response = await api.put(`${API_BASE}/model-pricing/${id}`, data);
    return response.data?.data || response.data;
  },

  async deleteModelPricing(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/model-pricing/${id}`);
    return response.data;
  },

  async previewModelPricingBackfill(id: string): Promise<{
    pricingId: string;
    provider: string;
    model: string;
    candidateCount: number;
    totalTokens: number;
    estimatedCostUsd: number;
    affectedUserCount: number;
  }> {
    const response = await api.get(
      `${API_BASE}/model-pricing/${id}/backfill-preview`,
    );
    return response.data?.data || response.data;
  },

  async applyModelPricingBackfill(id: string): Promise<{
    pricingId: string;
    updatedCount: number;
    totalCostApplied: number;
    invalidatedUserCount: number;
  }> {
    const response = await api.post(
      `${API_BASE}/model-pricing/${id}/backfill`,
    );
    return response.data?.data || response.data;
  },

  // Image Model Pricing
  async getImageModelPricings(): Promise<any[]> {
    const response = await api.get(`${API_BASE}/image-model-pricing`);
    return response.data?.data || response.data || [];
  },

  async getImageModelPricing(id: string): Promise<any> {
    const response = await api.get(`${API_BASE}/image-model-pricing/${id}`);
    return response.data?.data || response.data;
  },

  async createImageModelPricing(data: {
    provider: string;
    model: string;
    quality: string;
    size: string;
    costUsd: number;
  }): Promise<any> {
    const response = await api.post(`${API_BASE}/image-model-pricing`, data);
    return response.data?.data || response.data;
  },

  async updateImageModelPricing(
    id: string,
    data: {
      provider?: string;
      model?: string;
      quality?: string;
      size?: string;
      costUsd?: number;
    },
  ): Promise<any> {
    const response = await api.put(
      `${API_BASE}/image-model-pricing/${id}`,
      data,
    );
    return response.data?.data || response.data;
  },

  async deleteImageModelPricing(id: string): Promise<{ message: string }> {
    const response = await api.delete(
      `${API_BASE}/image-model-pricing/${id}`,
    );
    return response.data;
  },

  // Image Model Specs (capabilities)
  async getImageModelSpecs(): Promise<ImageModelSpec[]> {
    const response = await api.get(`${API_BASE}/image-model-specs`);
    return response.data?.data || response.data || [];
  },

  async createImageModelSpec(
    data: ImageModelSpecInput,
  ): Promise<ImageModelSpec> {
    const response = await api.post(`${API_BASE}/image-model-specs`, data);
    return response.data?.data || response.data;
  },

  async updateImageModelSpec(
    id: string,
    data: Partial<ImageModelSpecInput>,
  ): Promise<ImageModelSpec> {
    const response = await api.put(`${API_BASE}/image-model-specs/${id}`, data);
    return response.data?.data || response.data;
  },

  async deleteImageModelSpec(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/image-model-specs/${id}`);
    return response.data;
  },

  async getAllLLMModels(): Promise<any[]> {
    const response = await api.get(`${API_BASE}/models/all`);
    return response.data?.data || response.data || [];
  },

  async getGlobalModelConfig(): Promise<
    Record<string, { model: string | null; provider: string | null; config: Record<string, unknown> | null }>
  > {
    const response = await api.get(`${API_BASE}/models/global-config`);
    return response.data;
  },

  async testModel(
    type: string,
  ): Promise<{
    success: boolean;
    model: string;
    provider: string;
    response: string;
    latencyMs: number;
  }> {
    const response = await api.post(`${API_BASE}/models/test`, { type });
    return response.data;
  },

  // Product Models
  async getProductModels(): Promise<any[]> {
    const response = await api.get(`${API_BASE}/product-models`);
    return response.data?.data || response.data || [];
  },

  async createProductModel(data: {
    name: string;
    description?: string;
    modelName: string;
    providerId: string;
    displayOrder?: number;
    isActive?: boolean;
    isDefault?: boolean;
    tier?: number;
    bestAt?: string;
    fallbackLlmModelIds?: string[];
  }): Promise<any> {
    const response = await api.post(`${API_BASE}/product-models`, data);
    return response.data?.data || response.data;
  },

  async updateProductModel(
    id: string,
    data: {
      name?: string;
      description?: string;
      modelName?: string;
      providerId?: string;
      displayOrder?: number;
      isActive?: boolean;
      isDefault?: boolean;
      tier?: number;
      bestAt?: string;
      fallbackLlmModelIds?: string[];
    },
  ): Promise<any> {
    const response = await api.put(`${API_BASE}/product-models/${id}`, data);
    return response.data?.data || response.data;
  },

  async deleteProductModel(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/product-models/${id}`);
    return response.data;
  },

  async getRoutingHealth(): Promise<{
    rows: Array<{
      llmModelId: string | null;
      provider: string;
      modelName: string;
      displayName: string;
      referencedBy: string[];
      role: 'productModelPrimary' | 'fallback' | 'both';
      cooldownActive: boolean;
      cooldownExpiresAtMs: number | null;
      cooldownReason: string | null;
      keyRetired: boolean;
      manuallyDisabled: boolean;
      successCount: number;
      failureCount: number;
      totalCount: number;
      successRatePct: number | null;
      ttftP50Ms: number | null;
      ttftP90Ms: number | null;
      tpsP50: number | null;
      thresholds: {
        errorRateThresholdPct: number;
        ttftDegradedThresholdMs: number;
        ttftPerTokenMs: number;
        tpsDegradedThresholdMin: number;
        minSamplesForTrigger: number;
        latencyCooldownMs: number;
        recoverySkipAfterFailures: number;
        autoRetireAfterFailures: number;
      };
      isDegraded: boolean;
      degradedReasons: string[];
    }>;
    catalogSize: number;
  }> {
    const response = await api.get(`${API_BASE}/routing/health`);
    return response.data?.data || response.data;
  },

  async getRoutingChains(): Promise<{
    chains: Array<{
      productModelId: string;
      displayName: string;
      candidates: Array<{
        role: 'primary' | 'fallback' | 'orgLastResort';
        fallbackOrder: number;
        provider: string;
        modelName: string;
        displayName: string;
        productModelId: string | null;
        llmModelId: string | null;
        cooldownActive: boolean;
        cooldownExpiresAtMs: number | null;
        cooldownReason: string | null;
        keyRetired: boolean;
        manuallyDisabled: boolean;
        successCount: number;
        failureCount: number;
        totalCount: number;
        successRatePct: number | null;
        ttftP50Ms: number | null;
        ttftP90Ms: number | null;
        tpsP50: number | null;
        thresholds: {
          errorRateThresholdPct: number;
          ttftDegradedThresholdMs: number;
          ttftPerTokenMs: number;
          tpsDegradedThresholdMin: number;
          minSamplesForTrigger: number;
          latencyCooldownMs: number;
          recoverySkipAfterFailures: number;
          autoRetireAfterFailures: number;
        };
        isDegraded: boolean;
        degradedReasons: string[];
      }>;
    }>;
  }> {
    const response = await api.get(`${API_BASE}/routing/chains`);
    return response.data?.data || response.data;
  },

  async updateLLMModelThresholds(
    id: string,
    data: {
      errorRateThresholdPct?: number;
      ttftDegradedThresholdMs?: number;
      ttftPerTokenMs?: number;
      tpsDegradedThresholdMin?: number;
      minSamplesForTrigger?: number;
      latencyCooldownMs?: number;
      recoverySkipAfterFailures?: number;
      autoRetireAfterFailures?: number;
    },
  ): Promise<any> {
    const response = await api.put(`${API_BASE}/models/${id}`, data);
    return response.data?.data || response.data;
  },

  async getStickyPinSummary(): Promise<{
    totalPinned: number;
    sample: Array<{
      chatId: string;
      provider: string;
      modelName: string;
      productModelId?: string;
    }>;
  }> {
    const response = await api.get(`${API_BASE}/routing/sticky-pins`);
    return response.data?.data || response.data;
  },

  async clearRoutingCooldown(provider: string, modelName: string): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/routing/clear-cooldown`, { provider, modelName });
    return response.data;
  },

  async forceRoutingCooldown(
    provider: string,
    modelName: string,
    durationMs: number,
  ): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/routing/force-cooldown`, {
      provider,
      modelName,
      durationMs,
    });
    return response.data;
  },

  async unretireRoutingKey(provider: string, modelName: string): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/routing/unretire-key`, { provider, modelName });
    return response.data;
  },

  async forceRetireRoutingKey(
    provider: string,
    modelName: string,
  ): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/routing/force-retire`, { provider, modelName });
    return response.data;
  },

  async setCandidateEnabled(
    provider: string,
    modelName: string,
    enabled: boolean,
  ): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/routing/set-enabled`, {
      provider,
      modelName,
      enabled,
    });
    return response.data;
  },

  async getRoutingFeatureEnabled(): Promise<{ enabled: boolean }> {
    const response = await api.get(`${API_BASE}/routing/feature-enabled`);
    return response.data?.data || response.data;
  },

  async setRoutingFeatureEnabled(enabled: boolean): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/routing/feature-enabled`, { enabled });
    return response.data;
  },

  async getRoutingRecoveryStatus(): Promise<{
    workerState: 'active' | 'stalled' | 'never-run';
    lastRunMs: number | null;
    nextRunMs: number | null;
    intervalMs: number;
    recentEvents: Array<{
      timestampMs: number;
      provider: string;
      modelName: string;
      outcome: 'recovered' | 'extendedCooldown' | 'probeError' | 'autoRetired';
      consecutiveFailures?: number;
      backoffMs?: number;
      error?: string;
    }>;
    extendedCooldowns: Array<{
      provider: string;
      modelName: string;
      consecutiveFailures: number;
    }>;
  }> {
    const response = await api.get(`${API_BASE}/routing/recovery-status`);
    return response.data?.data || response.data;
  },

  async runRoutingRecoveryNow(): Promise<{
    candidatesProbed: number;
    recovered: number;
    extended: number;
    autoRetired: number;
  }> {
    const response = await api.post(`${API_BASE}/routing/recovery-run-now`);
    return response.data?.data || response.data;
  },

  async runRoutingProbe(productModelId: string): Promise<{
    productModelId: string;
    runAtMs: number;
    bestKey: string | null;
    worstKey: string | null;
    suggestion: string;
    results: Array<{
      provider: string;
      modelName: string;
      role: 'primary' | 'fallback' | 'orgLastResort';
      fallbackOrder: number;
      productModelId: string | null;
      success: boolean;
      ttftMs: number | null;
      durationMs: number;
      outputTokens: number | null;
      tpsTokensPerSec: number | null;
      errorClassification: string | null;
      errorMessage: string | null;
      actionTaken: 'markedCooldown' | 'retiredKey' | 'none';
      score: number | null;
    }>;
  }> {
    const response = await api.post(`${API_BASE}/routing/probe`, { productModelId });
    return response.data?.data || response.data;
  },

  // Waitlist & Invites
  async getWaitlistUsers(
    filters: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {},
  ): Promise<{
    users: User[];
    pagination: { page: number; limit: number; total: number; pages: number };
    counts: { all: number; waitlisted: number; active: number };
  }> {
    const response = await api.get(`${API_BASE}/waitlist`, { params: filters });
    return response.data;
  },

  async approveWaitlistedUser(
    userId: string,
  ): Promise<{ message: string; invite: Invite }> {
    const response = await api.post(`${API_BASE}/waitlist/${userId}/approve`);
    return response.data;
  },

  async revokeUserAccess(userId: string): Promise<{ message: string }> {
    const response = await api.post(`${API_BASE}/waitlist/${userId}/revoke`);
    return response.data;
  },

  async getInvites(
    page: number = 1,
    limit: number = 50,
  ): Promise<{
    invites: Invite[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const response = await api.get(`${API_BASE}/invites`, {
      params: { page, limit },
    });
    return response.data;
  },

  async createInvite(data: {
    email: string;
    name?: string;
  }): Promise<{ message: string; invite: Invite }> {
    const response = await api.post(`${API_BASE}/invite`, data);
    return response.data;
  },

  async deleteInvite(id: string): Promise<{ message: string }> {
    const response = await api.delete(`${API_BASE}/invites/${id}`);
    return response.data;
  },


  async getEmailAssistantConfig(): Promise<EmailAssistantGlobalConfig> {
    const response = await api.get(`${API_BASE}/email-assistant-config`);
    return response.data;
  },

  async updateEmailAssistantConfig(
    data: Partial<EmailAssistantGlobalConfig>,
  ): Promise<EmailAssistantGlobalConfig> {
    const response = await api.put(`${API_BASE}/email-assistant-config`, data);
    return response.data;
  },

  async getIngestionScopeConfig(): Promise<IngestionScopeConfig> {
    const response = await api.get(`${API_BASE}/ingestion-scope-config`);
    return response.data;
  },

  async updateIngestionScopeConfig(
    data: IngestionScopeConfigUpdate,
  ): Promise<IngestionScopeConfig> {
    const response = await api.put(`${API_BASE}/ingestion-scope-config`, data);
    return response.data;
  },

  async triggerConnectorFullBackfill(
    id: string,
  ): Promise<{ status: string; message: string }> {
    const response = await api.post(
      `${API_BASE}/connectors/${id}/full-backfill`,
    );
    return response.data;
  },

  async getWorkerStatus(): Promise<any> {
    const response = await api.get(`${API_BASE}/workers/status`);
    return response.data;
  },

  async getFeatureFlags(): Promise<FeatureFlagsGlobalResponse> {
    const response = await api.get(`${API_BASE}/feature-flags`);
    return response.data;
  },

  async updateFeatureFlags(
    flags: Partial<Record<string, boolean>>,
  ): Promise<FeatureFlagsGlobalResponse> {
    const response = await api.put(`${API_BASE}/feature-flags`, flags);
    return response.data;
  },

  async searchFeatureFlagOrganizations(
    query: string,
  ): Promise<FeatureFlagOrganizationsResponse> {
    const response = await api.get(`${API_BASE}/feature-flags/organizations/search`, {
      params: { q: query },
    });
    return response.data;
  },

  async getFeatureFlagOrganizations(
    query: string = "",
    limit: number = 50,
  ): Promise<FeatureFlagOrganizationsResponse> {
    const response = await api.get(`${API_BASE}/feature-flags/organizations`, {
      params: { q: query, limit },
    });
    return response.data;
  },

  async getOrganizationFeatureFlags(
    organizationId: string,
  ): Promise<OrganizationFeatureFlagsResponse> {
    const response = await api.get(
      `${API_BASE}/feature-flags/organizations/${organizationId}`,
    );
    return response.data;
  },

  async setOrganizationFeatureFlagOverride(
    organizationId: string,
    key: string,
    enabled: boolean,
  ): Promise<OrganizationFeatureFlagsResponse> {
    const response = await api.put(
      `${API_BASE}/feature-flags/organizations/${organizationId}`,
      { key, enabled },
    );
    return response.data;
  },

  async clearOrganizationFeatureFlagOverride(
    organizationId: string,
    key: string,
  ): Promise<OrganizationFeatureFlagsResponse> {
    const response = await api.delete(
      `${API_BASE}/feature-flags/organizations/${organizationId}/${key}`,
    );
    return response.data;
  },

  async syncOrganizationFeatureFlagOverrides(
    organizationId: string,
  ): Promise<OrganizationFeatureFlagSyncResponse> {
    const response = await api.post(
      `${API_BASE}/feature-flags/organizations/${organizationId}/sync`,
    );
    return response.data;
  },

  async bulkUpdateOrganizationFeatureFlagOverrides(
    organizationIds: string[],
    key: string,
    action: FeatureFlagOverrideState,
  ): Promise<BulkOrganizationFeatureFlagOverrideResponse> {
    const response = await api.post(
      `${API_BASE}/feature-flags/organizations/bulk`,
      { organizationIds, key, action },
    );
    return response.data;
  },

  async bulkSyncOrganizationFeatureFlagOverrides(
    organizationIds: string[],
  ): Promise<BulkOrganizationFeatureFlagSyncResponse> {
    const response = await api.post(
      `${API_BASE}/feature-flags/organizations/bulk-sync`,
      { organizationIds },
    );
    return response.data;
  },

  async getConnectorFlags(): Promise<{
    disabledConnectorTypes: string[];
    allConnectorTypes: string[];
  }> {
    const response = await api.get(`${API_BASE}/connector-flags`);
    return response.data;
  },

  async updateConnectorFlags(
    disabledConnectorTypes: string[],
  ): Promise<{
    disabledConnectorTypes: string[];
    allConnectorTypes: string[];
  }> {
    const response = await api.put(`${API_BASE}/connector-flags`, {
      disabledConnectorTypes,
    });
    return response.data;
  },

  async getSidebarConfig(): Promise<{ items: SidebarItem[] }> {
    const response = await api.get(`${API_BASE}/sidebar-config`);
    return response.data;
  },

  async updateSidebarConfig(
    items: SidebarItem[],
  ): Promise<{ items: SidebarItem[] }> {
    const response = await api.put(`${API_BASE}/sidebar-config`, { items });
    return response.data;
  },

  async getRateLimitConfig(): Promise<RateLimitConfig> {
    const response = await api.get(`${API_BASE}/rate-limit-config`);
    return response.data;
  },

  async updateRateLimitConfig(
    data: Partial<Omit<RateLimitConfig, "updatedAt">>,
  ): Promise<RateLimitConfig> {
    const response = await api.put(`${API_BASE}/rate-limit-config`, data);
    return response.data;
  },

  async getPlanConfigs(): Promise<SubscriptionPlanConfig[]> {
    const response = await api.get(`${API_BASE}/plan-configs`);
    return response.data;
  },

  async updatePlanConfig(
    plan: string,
    data: Partial<Omit<SubscriptionPlanConfig, "plan" | "updatedAt">>,
  ): Promise<SubscriptionPlanConfig> {
    const response = await api.put(`${API_BASE}/plan-configs/${plan}`, data);
    return response.data;
  },

  async updateUserPlan(userId: string, plan: string): Promise<{ message: string; userId: string; plan: string }> {
    const response = await api.put(`${API_BASE}/users/${userId}/plan`, { plan })
    return response.data
  },

  async getEmbeddingStats(): Promise<EmbeddingRouterStats> {
    const response = await api.get(`${API_BASE}/embedding/stats`);
    return response.data;
  },

  async getGpuConfig(): Promise<{
    useT4Gpu: boolean;
    forceP0Serverless: boolean;
    serverlessConfigured: boolean;
  }> {
    const response = await api.get(`${API_BASE}/gpu-config`);
    return response.data;
  },

  async updateGpuConfig(
    data: Partial<{ useT4Gpu: boolean; forceP0Serverless: boolean }>,
  ): Promise<{
    useT4Gpu: boolean;
    forceP0Serverless: boolean;
    serverlessConfigured: boolean;
  }> {
    const response = await api.put(`${API_BASE}/gpu-config`, data);
    return response.data;
  },

  // Native Abilities
  async getNativeAbilities(): Promise<any[]> {
    const response = await api.get(`${API_BASE}/abilities`);
    return response.data?.abilities || response.data?.data || [];
  },

  async updateNativeAbility(
    id: string,
    data: {
      name?: string;
      description?: string;
      instructions?: string;
      icon?: string;
      toolPreferences?: string[];
    },
  ): Promise<any> {
    const response = await api.put(`${API_BASE}/abilities/${id}`, data);
    return response.data?.data || response.data;
  },

  async createNativeAbility(data: {
    id: string;
    name: string;
    description: string;
    instructions: string;
    icon?: string;
    toolPreferences?: string[];
  }): Promise<any> {
    const response = await api.post(`${API_BASE}/abilities`, data);
    return response.data?.data || response.data;
  },

  async deleteNativeAbility(id: string): Promise<void> {
    await api.delete(`${API_BASE}/abilities/${id}`);
  },

  // Native Agents Management
  async getNativeAgents(): Promise<NativeAgentRow[]> {
    const response = await api.get(`${API_BASE}/native-agents`);
    return response.data?.agents ?? [];
  },

  async reseedNativeAgents(): Promise<{ seeded: string[]; count: number }> {
    const response = await api.post(`${API_BASE}/native-agents/reseed`);
    return response.data;
  },

  async patchNativeAgent(
    slug: string,
    data: { isActive?: boolean; productModelId?: string | null },
  ): Promise<NativeAgentRow> {
    const response = await api.patch(`${API_BASE}/native-agents/${slug}`, data);
    return response.data?.agent;
  },

  async getCustomAgents(
    page = 1,
    pageSize = 50,
  ): Promise<{ agents: CustomAgentRow[]; pagination: { page: number; pageSize: number; total: number; pages: number } }> {
    const response = await api.get(`${API_BASE}/custom-agents`, { params: { page, pageSize } });
    return response.data;
  },

  // Operation Logs (Observability)
  async getOperationLogs(filters: {
    operationType?: string;
    isSlow?: boolean;
    userId?: string;
    organizationId?: string;
    chatId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    logs: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params: Record<string, string> = {};
    if (filters.operationType) params.operationType = filters.operationType;
    if (filters.isSlow !== undefined) params.isSlow = String(filters.isSlow);
    if (filters.userId) params.userId = filters.userId;
    if (filters.organizationId) params.organizationId = filters.organizationId;
    if (filters.chatId) params.chatId = filters.chatId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.page) params.page = String(filters.page);
    if (filters.limit) params.limit = String(filters.limit);
    const response = await api.get(`${API_BASE}/operation-logs`, { params });
    return response.data;
  },

  async getOperationLogStats(filters: {
    operationType?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{
    totalCount: number;
    slowCount: number;
    avgDurationMs: number;
    p50Ms: number;
    p95Ms: number;
    p99Ms: number;
  }> {
    const response = await api.get(`${API_BASE}/operation-logs/stats`, { params: filters });
    return response.data;
  },

  async purgeOperationLogs(olderThanDays: number): Promise<{ deletedCount: number }> {
    const response = await api.delete(`${API_BASE}/operation-logs/purge`, {
      params: { olderThanDays },
    });
    return response.data;
  },

  // Slide Generation Traces (Observability)
  async getSlideTraces(filters: {
    status?: SlideGenerationTraceStatus | "";
    userId?: string;
    organizationId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<SlideTracesListResponse> {
    const params: Record<string, string> = {};
    if (filters.status) params.status = filters.status;
    if (filters.userId) params.userId = filters.userId;
    if (filters.organizationId) params.organizationId = filters.organizationId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.page) params.page = String(filters.page);
    if (filters.limit) params.limit = String(filters.limit);
    const response = await api.get(`${API_BASE}/slide-traces`, { params });
    return response.data;
  },

  async getSlideTraceById(id: string): Promise<SlideGenerationTrace> {
    const response = await api.get(`${API_BASE}/slide-traces/${id}`);
    return response.data;
  },

  // Communications
  async getDeliveries(filters: {
    cursor?: string;
    limit?: number;
    channel?: string;
    status?: string;
    templateKey?: string;
    recipient?: string;
    userId?: string;
    from?: string;
    to?: string;
  } = {}): Promise<import("../types/communications").DeliveryListResponse> {
    const params: Record<string, string> = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params[k] = String(v);
    });
    const response = await api.get(`${API_BASE}/communications/deliveries`, {
      params,
    });
    return response.data;
  },

  async getDeliveryById(
    id: string
  ): Promise<import("../types/communications").NotificationDelivery> {
    const response = await api.get(
      `${API_BASE}/communications/deliveries/${id}`
    );
    return response.data;
  },

  async getDeliveryStats(): Promise<
    import("../types/communications").DeliveryStats
  > {
    const response = await api.get(`${API_BASE}/communications/stats`);
    return response.data;
  },

  async getDeliveryTemplateKeys(): Promise<{ templateKeys: string[] }> {
    const response = await api.get(
      `${API_BASE}/communications/template-keys`
    );
    return response.data;
  },

  async getSuppressions(filters: {
    limit?: number;
    reason?: string;
  } = {}): Promise<{ items: import("../types/communications").SuppressionEntry[] }> {
    const params: Record<string, string> = {};
    if (filters.limit) params.limit = String(filters.limit);
    if (filters.reason) params.reason = filters.reason;
    const response = await api.get(`${API_BASE}/communications/suppressions`, {
      params,
    });
    return response.data;
  },

  async addSuppression(input: {
    email: string;
    reason?: string;
    userId?: string;
  }): Promise<{ ok: true }> {
    const response = await api.post(
      `${API_BASE}/communications/suppressions`,
      input
    );
    return response.data;
  },

  async removeSuppression(email: string): Promise<{ ok: true }> {
    const response = await api.delete(
      `${API_BASE}/communications/suppressions/${encodeURIComponent(email)}`
    );
    return response.data;
  },

  // Settings
  async getCommSettings(): Promise<
    import("../types/communications").CommSettings
  > {
    const response = await api.get(`${API_BASE}/communications/settings`);
    return response.data;
  },

  async updateCommSettings(
    input: Partial<import("../types/communications").CommSettings>
  ): Promise<import("../types/communications").CommSettings> {
    const response = await api.put(`${API_BASE}/communications/settings`, input);
    return response.data;
  },

  // Templates
  async getEmailTemplates(params?: {
    category?: string;
    source?: string;
  }): Promise<{
    items: import("../types/communications").EmailTemplate[];
  }> {
    const response = await api.get(`${API_BASE}/communications/templates`, {
      params,
    });
    return response.data;
  },

  async createEmailTemplate(
    input: Partial<import("../types/communications").EmailTemplate>
  ): Promise<
    import("../types/communications").EmailTemplate & {
      sanitizationWarnings: string[];
    }
  > {
    const response = await api.post(
      `${API_BASE}/communications/templates`,
      input
    );
    return response.data;
  },

  async updateEmailTemplate(
    id: string,
    input: Partial<import("../types/communications").EmailTemplate>
  ): Promise<
    import("../types/communications").EmailTemplate & {
      sanitizationWarnings: string[];
    }
  > {
    const response = await api.put(
      `${API_BASE}/communications/templates/${id}`,
      input
    );
    return response.data;
  },

  async deleteEmailTemplate(
    id: string
  ): Promise<
    | { ok: true }
    | { error: string; broadcastRefs: number; campaignRefs: number }
  > {
    const response = await api.delete(
      `${API_BASE}/communications/templates/${id}`
    );
    return response.data;
  },

  async previewTemplate(
    id: string,
    slotValues?: Record<string, unknown>
  ): Promise<import("../types/communications").TemplatePreviewResult> {
    const response = await api.post(
      `${API_BASE}/communications/templates/${id}/preview`,
      { slotValues }
    );
    return response.data;
  },

  async testSendTemplate(
    id: string,
    to: string,
    slotValues?: Record<string, unknown>
  ): Promise<{ sent: number; suppressed: number; failed: number }> {
    const response = await api.post(
      `${API_BASE}/communications/templates/${id}/test-send`,
      { to, slotValues }
    );
    return response.data;
  },

  // AI
  async aiGenerateTemplate(
    prompt: string,
    category: import("../types/communications").TemplateCategory
  ): Promise<import("../types/communications").GenerateTemplateDraftResult> {
    const response = await api.post(
      `${API_BASE}/communications/ai/generate-template`,
      { prompt, category }
    );
    return response.data;
  },

  async aiGenerateContent(
    templateId: string,
    prompt: string,
    extras?: { tone?: string; length?: string; locale?: string }
  ): Promise<import("../types/communications").SlotContentResult> {
    const response = await api.post(
      `${API_BASE}/communications/ai/generate-content`,
      { templateId, prompt, ...(extras ?? {}) }
    );
    return response.data;
  },

  async aiRefineContent(
    templateId: string,
    slotValues: Record<string, unknown>,
    instruction: string
  ): Promise<import("../types/communications").SlotContentResult> {
    const response = await api.post(
      `${API_BASE}/communications/ai/refine-content`,
      { templateId, slotValues, instruction }
    );
    return response.data;
  },

  // Lifecycle campaigns
  async getCampaigns(): Promise<{
    items: import("../types/communications").LifecycleCampaign[];
  }> {
    const response = await api.get(`${API_BASE}/communications/campaigns`);
    return response.data;
  },

  async updateCampaign(
    type: string,
    input: Partial<import("../types/communications").LifecycleCampaign>
  ): Promise<import("../types/communications").LifecycleCampaign> {
    const response = await api.put(
      `${API_BASE}/communications/campaigns/${type}`,
      input
    );
    return response.data;
  },

  // Broadcasts
  async getBroadcasts(): Promise<{
    items: import("../types/communications").Broadcast[];
  }> {
    const response = await api.get(`${API_BASE}/communications/broadcasts`);
    return response.data;
  },

  async createBroadcast(input: {
    title: string;
    subject?: string;
    bodyHtml?: string;
    bodyText?: string;
    audience: string;
    audienceFilter?: Record<string, unknown>;
    scheduledAt?: string;
    sendNow?: boolean;
    experimentKey?: string;
    templateId?: string | null;
    slotValues?: Record<string, unknown> | null;
  }): Promise<import("../types/communications").Broadcast> {
    const response = await api.post(
      `${API_BASE}/communications/broadcasts`,
      input
    );
    return response.data;
  },

  async updateBroadcast(
    id: string,
    input: {
      title?: string;
      subject?: string;
      bodyHtml?: string;
      bodyText?: string;
      audience?: string;
      audienceFilter?: Record<string, unknown>;
      scheduledAt?: string | null;
      experimentKey?: string | null;
      templateId?: string | null;
      slotValues?: Record<string, unknown> | null;
    },
  ): Promise<import("../types/communications").Broadcast> {
    const response = await api.put(
      `${API_BASE}/communications/broadcasts/${id}`,
      input
    );
    return response.data;
  },

  async sendBroadcast(id: string): Promise<{ ok: true }> {
    const response = await api.post(
      `${API_BASE}/communications/broadcasts/${id}/send`
    );
    return response.data;
  },

  // Digest
  async getDigestConfig(): Promise<
    import("../types/communications").DigestConfig
  > {
    const response = await api.get(`${API_BASE}/communications/digest/config`);
    return response.data;
  },

  async updateDigestConfig(
    input: Partial<import("../types/communications").DigestConfig>
  ): Promise<unknown> {
    const response = await api.put(
      `${API_BASE}/communications/digest/config`,
      input
    );
    return response.data;
  },

  async previewDigest(input: {
    userId?: string;
    windowDays?: number;
  }): Promise<{
    userId: string;
    since: string;
    summary: import("../types/communications").DigestPreviewSummary;
  }> {
    const response = await api.post(
      `${API_BASE}/communications/digest/preview`,
      input
    );
    return response.data;
  },

  async sendDigestToMe(): Promise<{ sent: boolean; reason?: string }> {
    const response = await api.post(
      `${API_BASE}/communications/digest/send-to-me`
    );
    return response.data;
  },

  async runDigestSweep(): Promise<{
    daySent: number;
    weekSent: number;
    skipped: number;
  }> {
    const response = await api.post(
      `${API_BASE}/communications/digest/run-sweep`
    );
    return response.data;
  },

  // Experiments
  async getExperiments(): Promise<{
    items: import("../types/communications").CommExperiment[];
  }> {
    const response = await api.get(`${API_BASE}/communications/experiments`);
    return response.data;
  },

  async createExperiment(input: {
    key: string;
    variants: import("../types/communications").ExperimentVariant[];
    notes?: string;
    status?: import("../types/communications").CommExperimentStatus;
  }): Promise<import("../types/communications").CommExperiment> {
    const response = await api.post(
      `${API_BASE}/communications/experiments`,
      input
    );
    return response.data;
  },

  async updateExperiment(
    id: string,
    input: Partial<import("../types/communications").CommExperiment>
  ): Promise<import("../types/communications").CommExperiment> {
    const response = await api.put(
      `${API_BASE}/communications/experiments/${id}`,
      input
    );
    return response.data;
  },

  async deleteExperiment(id: string): Promise<{ ok: true }> {
    const response = await api.delete(
      `${API_BASE}/communications/experiments/${id}`
    );
    return response.data;
  },

  async getExperimentResults(
    key: string
  ): Promise<{ stats: import("../types/communications").VariantStats[] }> {
    const response = await api.get(
      `${API_BASE}/communications/experiments/${encodeURIComponent(key)}/results`
    );
    return response.data;
  },

  async getReferrals(filters: {
    page?: number;
    pageSize?: number;
    status?: 'pending' | 'accepted';
    search?: string;
    since?: string;
  } = {}): Promise<{
    items: Array<{
      id: string;
      email: string;
      status: 'pending' | 'accepted';
      createdAt: string;
      acceptedAt: string | null;
      referrer: { id: string; email: string; name: string | null };
      claimedBy: { id: string; email: string; name: string | null } | null;
    }>;
    pagination: { page: number; pageSize: number; total: number; pages: number };
    metrics: {
      totalSent: number;
      totalAccepted: number;
      totalPending: number;
      conversionRate: number;
    };
  }> {
    const response = await api.get(`${API_BASE}/referrals`, { params: filters });
    return response.data;
  },

  async revokeReferral(id: string): Promise<{ ok: true }> {
    const response = await api.delete(`${API_BASE}/referrals/${id}`);
    return response.data;
  },

  // ── Support Requests (Internal Triage) ──────────────────────────────────
  async getSupportRequests(
    filters: Record<string, string> = {},
  ): Promise<{ requests: SupportRequestRow[] }> {
    const response = await api.get(`${API_BASE}/support`, { params: filters });
    return response.data;
  },

  async getSupportRequest(id: string): Promise<SupportRequestDetail> {
    const response = await api.get(`${API_BASE}/support/${id}`);
    return response.data;
  },

  async replySupportRequest(id: string, body: string): Promise<unknown> {
    const response = await api.post(`${API_BASE}/support/${id}/messages`, { body });
    return response.data;
  },

  async updateSupportStatus(id: string, status: string): Promise<SupportRequestDetail> {
    const response = await api.patch(`${API_BASE}/support/${id}/status`, { status });
    return response.data;
  },

  async updateSupportSeverity(id: string, severity: string): Promise<SupportRequestDetail> {
    const response = await api.patch(`${API_BASE}/support/${id}/severity`, { severity });
    return response.data;
  },

  async createSupportLinearIssue(id: string): Promise<SupportRequestDetail> {
    const response = await api.post(`${API_BASE}/support/${id}/linear-issue`);
    return response.data;
  },

  async getSupportAttachmentDownloadUrl(
    id: string,
    attachmentId: string,
  ): Promise<{ url: string; fileName: string }> {
    const response = await api.get(
      `${API_BASE}/support/${id}/attachments/${attachmentId}/download`,
    );
    return response.data;
  },

  async getProcessingIncidents(
    filters: ProcessingIncidentFilters = {},
  ): Promise<ProcessingIncidentListResponse> {
    const response = await api.get(`${API_BASE}/support/incidents`, {
      params: filters,
    });
    return response.data;
  },

  async getProcessingIncident(
    id: string,
    filters: ProcessingIncidentDetailFilters = {},
  ): Promise<ProcessingIncidentDetail> {
    const response = await api.get(`${API_BASE}/support/incidents/${id}`, {
      params: filters,
    });
    return response.data;
  },

  async updateProcessingIncidentStatus(
    id: string,
    status: ProcessingIncidentStatus,
  ): Promise<ProcessingIncidentStatusResponse> {
    const response = await api.patch(
      `${API_BASE}/support/incidents/${id}/status`,
      { status },
    );
    return response.data;
  },

  async triggerTestProcessingIncident(
    body: {
      incidentSurface: ProcessingIncidentSurface;
      incidentOperation: ProcessingIncidentOperation;
    },
  ): Promise<ProcessingIncidentTestTriggerResponse> {
    const response = await api.post(
      `${API_BASE}/support/incidents/test-trigger`,
      body,
    );
    return response.data;
  },

  async deleteProcessingIncident(
    id: string,
  ): Promise<ProcessingIncidentDeleteResponse> {
    const response = await api.delete(`${API_BASE}/support/incidents/${id}`);
    return response.data;
  },

  async createProcessingIncidentLinearIssue(
    id: string,
  ): Promise<ProcessingIncidentLinearIssueResponse> {
    const response = await api.post(
      `${API_BASE}/support/incidents/${id}/linear-issue`,
    );
    return response.data;
  },

  async linkProcessingIncidentLinearIssue(
    id: string,
    payload: ProcessingIncidentLinearLinkPayload,
  ): Promise<ProcessingIncidentLinearIssueResponse> {
    const response = await api.post(
      `${API_BASE}/support/incidents/${id}/linear-link`,
      payload,
    );
    return response.data;
  },
}

export interface SupportRequestRow {
  id: string;
  type: string;
  status: string;
  severity: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
  needsReply: boolean;
  user?: { id: string; email: string; name: string | null } | null;
  organization?: { id: string; name: string } | null;
}

export interface SupportRequestDetail extends SupportRequestRow {
  description: string;
  context: Record<string, unknown> | null;
  correlationId: string | null;
  traceparent: string | null;
  releaseVersion: string | null;
  environment: string | null;
  routeUrl: string | null;
  chatId: string | null;
  messageId: string | null;
  assistantMessageId: string | null;
  reasonTags: string[];
  externalIssueProvider: string | null;
  externalIssueIdentifier: string | null;
  externalIssueUrl: string | null;
  messages: {
    id: string;
    authorType: string;
    authorUserId: string | null;
    body: string;
    createdAt: string;
  }[];
  attachments: { id: string; fileName: string; mimeType: string }[];
  statusEvents: {
    id: string;
    fromStatus: string | null;
    toStatus: string;
    actorUserId: string | null;
    createdAt: string;
  }[];
}

export type ProcessingIncidentStatus =
  | "OPEN"
  | "TRIAGED"
  | "LINKED_TO_ISSUE"
  | "RESOLVED"
  | "IGNORED";

export type ProcessingIncidentPriority = "HIGH" | "URGENT";

// Incident product taxonomy (ADR-0065). Duplicated from @shared/observability's
// INCIDENT_SURFACE_VALUES / INCIDENT_OPERATION_VALUES because this repo is a
// standalone SPA outside the pnpm workspace (same cross-repo duplication as
// placeholders.ts) — KEEP IN SYNC with the server taxonomy.
export type ProcessingIncidentSurface =
  | "chat"
  | "settings"
  | "workspace"
  | "team_members"
  | "billing"
  | "integrations"
  | "connectors"
  | "file_upload"
  | "document_processing"
  | "preview"
  | "libra_apps"
  | "calendar_sync"
  | "email_assistant"
  | "meetings"
  | "workflows"
  | "dashboard"
  | "share_export"
  | "notifications";

export type ProcessingIncidentOperation =
  | "chat.request_failed"
  | "settings.save_failed"
  | "workspace.switch_failed"
  | "team_members.invite_failed"
  | "team_members.role_update_failed"
  | "billing.portal_failed"
  | "billing.subscription_update_failed"
  | "integrations.connect_failed"
  | "integrations.oauth_callback_failed"
  | "connectors.sync_failed"
  | "file_upload.upload_failed"
  | "document_processing.parse_failed"
  | "document_processing.index_failed"
  | "preview.render_failed"
  | "preview.load_failed"
  | "libra_apps.render_failed"
  | "libra_apps.action_failed"
  | "calendar_sync.connect_failed"
  | "calendar_sync.sync_failed"
  | "email_assistant.generate_failed"
  | "email_assistant.send_failed"
  | "meetings.transcript_failed"
  | "meetings.summary_failed"
  | "workflows.execution_failed"
  | "dashboard.load_failed"
  | "share_export.export_failed"
  | "notifications.delivery_failed";

export interface ProcessingIncidentFilters {
  status?: ProcessingIncidentStatus | "";
  priority?: ProcessingIncidentPriority | "";
  incidentSurface?: ProcessingIncidentSurface | "";
  incidentOperation?: ProcessingIncidentOperation | "";
  // Tri-state test filter sent as the literal query strings the server expects.
  isTest?: "true" | "false" | "";
  organizationId?: string;
  userId?: string;
  chatId?: string;
  correlationId?: string;
  // ISO date strings for the lastSeenAt range.
  from?: string;
  to?: string;
  // Free-text search over affected user/org id + error summary.
  q?: string;
  cursor?: string;
  take?: number;
}

export type ProcessingIncidentDetailFilters = Omit<
  ProcessingIncidentFilters,
  "status" | "priority"
>;

export interface ProcessingIncidentUser {
  id: string;
  email: string | null;
  name: string | null;
}

export interface ProcessingIncidentOrganization {
  id: string;
  name: string | null;
}

export interface ProcessingIncidentRow {
  id: string;
  fingerprint: string;
  incidentSurface: ProcessingIncidentSurface;
  incidentOperation: ProcessingIncidentOperation;
  isTest: boolean;
  source: string;
  method: string;
  endpoint: string;
  errorName: string | null;
  errorCode: string | null;
  errorSummary: string;
  status: ProcessingIncidentStatus;
  priority: ProcessingIncidentPriority;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrenceCount: number;
  affectedUserCount: number;
  affectedOrganizationCount: number;
  affectedUserIds: string[];
  affectedOrganizationIds: string[];
  affectedUsers: ProcessingIncidentUser[];
  affectedOrganizations: ProcessingIncidentOrganization[];
  lastReviewedAt: string | null;
  reviewSignalAt: string | null;
  reviewSignalCount: number;
  slackNotifiedAt: string | null;
  slackNotifyError: string | null;
  urgentEscalatedAt: string | null;
  urgentSlackNotifiedAt: string | null;
  urgentSlackNotifyError: string | null;
  externalIssueProvider: string | null;
  externalIssueId: string | null;
  externalIssueIdentifier: string | null;
  externalIssueUrl: string | null;
  linearSyncError: string | null;
  linearStateWarning: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessingIncidentOccurrence {
  id: string;
  processingIncidentId: string;
  incidentSurface: ProcessingIncidentSurface;
  incidentOperation: ProcessingIncidentOperation;
  isTest: boolean;
  organizationId: string;
  userId: string;
  organization: ProcessingIncidentOrganization | null;
  user: ProcessingIncidentUser | null;
  chatId: string | null;
  messageId: string | null;
  assistantMessageId: string | null;
  routeUrl: string | null;
  endpoint: string;
  method: string;
  correlationId: string | null;
  traceparent: string | null;
  errorDetail: string | null;
  stackHint: string | null;
  metadata: unknown;
  occurredAt: string;
  createdAt: string;
}

export interface ProcessingIncidentListResponse {
  incidents: ProcessingIncidentRow[];
  nextCursor: string | null;
}

export interface ProcessingIncidentDetail extends ProcessingIncidentRow {
  occurrences: ProcessingIncidentOccurrence[];
  occurrenceNextCursor: string | null;
}

export interface ProcessingIncidentStatusResponse {
  id: string;
  status: ProcessingIncidentStatus;
  priority: ProcessingIncidentPriority;
  errorSummary: string;
  reviewSignalAt: string | null;
  reviewSignalCount: number;
  lastReviewedAt: string | null;
}

export interface ProcessingIncidentTestTriggerResponse {
  captured: boolean;
  incidentId: string | null;
  incidentSurface: ProcessingIncidentSurface;
  incidentOperation: ProcessingIncidentOperation;
  isTest: true;
}

export interface ProcessingIncidentDeleteResponse {
  id: string;
  deleted: true;
}

export interface ProcessingIncidentLinearIssueResponse {
  id: string;
  status: ProcessingIncidentStatus;
  externalIssueProvider: string | null;
  externalIssueId: string | null;
  externalIssueIdentifier: string | null;
  externalIssueUrl: string | null;
}

export interface ProcessingIncidentLinearLinkPayload {
  externalIssueId: string;
  externalIssueIdentifier: string;
  externalIssueUrl: string;
}
